import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { unifiedSearch, ServerSearchType } from "../../api/searchAPI";
import { useTheme } from "../../context/ThemeContext";
import { getInputStyles, getCardBackground, getSecondaryText, getMutedText } from "../../utils/themeUtils";
import { captureError } from "../../utils/sentry";
import { isMemberRole } from "../../utils/roles";
import { rankSearchItems } from "../../search/rankSearchItems";
import type { SearchItem, SearchGroup } from "../../search/searchTypes";
import { useChat } from "../../context/ChatContext";
import Tooltip from "./Tooltip";
import { formatPersonName, hasName, UNNAMED_LEAD } from "../../utils/personName";
import { membershipPagePath } from "../../utils/membershipLinks";
import { Button } from "../ui";

const pathParts = window.location.pathname.split("/").filter(Boolean);
const tenantId = pathParts[0] || null;

const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);

type UserType = {
  id: string;
  // A lead captured by the AI chat can have no name at all.
  first_name: string | null;
  last_name?: string | null;
  email: string | null;
  phone?: string | null;
  status?: string;
  type: "member" | "athlete" | "customer" | "lead";
};

interface SearchBarProps {
  /**
   * The static, already-permission-filtered index (pages + reports + settings
   * + KB Help) the layout passes in. People are searched server-side; these
   * are matched instantly client-side. Omitted → people-only (back-compat).
   */
  pages?: SearchItem[];
  /**
   * Which server people categories to search (`types` on GET /search).
   * This is THE knob that makes the one SearchBar reusable per surface
   * (Dan 2026-08-27): the sidebar omits it (members+customers+athletes),
   * the memberships page adds leads, a KB page could pass just what fits.
   */
  serverTypes?: ServerSearchType[];
  placeholder?: string;
  /**
   * Notifies the host page of every query change, so an embedding page
   * (memberships/athletes tables) can drive its own list filter from the
   * same input the dropdown uses.
   */
  onQueryChange?: (query: string) => void;
  /** Passthrough for the onboarding walkthrough anchor. */
  dataOnboarding?: string;
  /** Seed the input (e.g. a Redux-persisted table filter). Uncontrolled after mount. */
  initialQuery?: string;
  /**
   * Register the global ⌘K/Ctrl-K focus shortcut. The sidebar instance owns
   * it; embedded page instances should pass false so they don't steal it.
   */
  enableShortcut?: boolean;
  /** Focus the input on mount (the top bar's search dialog opens with it). */
  autoFocus?: boolean;
}

const LOCAL_GROUP_ORDER: { key: SearchGroup; label: string }[] = [
  { key: "Page", label: "Pages" },
  { key: "Report", label: "Reports" },
  { key: "Setting", label: "Settings" },
  { key: "Help", label: "Help" },
];

// Server people results render grouped by category, in this order.
const PERSON_GROUP_ORDER: { type: UserType["type"]; label: string }[] = [
  { type: "member", label: "Members" },
  { type: "customer", label: "Customers" },
  { type: "lead", label: "Leads" },
  { type: "athlete", label: "Athletes" },
];

// Flat keyboard-navigable entry (people first, then local groups in order).
type FlatEntry =
  | { kind: "person"; person: UserType }
  | { kind: "local"; item: SearchItem };

function getStatusDotColor(status?: string): string {
  switch (status) {
    case "active":
    case "enrolled":
      return "bg-green-500";
    case "inactive":
      return "bg-gray-400";
    case "pending":
      return "bg-yellow-500";
    case "suspended":
      return "bg-red-500";
    case "archived":
      return "bg-gray-300";
    default:
      return "bg-gray-400";
  }
}

function SearchBar({
  pages = [],
  serverTypes,
  placeholder = "Search Anything",
  onQueryChange,
  dataOnboarding,
  initialQuery = "",
  enableShortcut = true,
  autoFocus = false,
}: SearchBarProps) {
  const [query, setQuery] = useState<string>(initialQuery);
  const [suggestions, setSuggestions] = useState<UserType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
    // Mount only: the dialog that asks for focus mounts this fresh each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // HELPER
  const token = localStorage.getItem("authToken");
  const userRole = localStorage.getItem("userRole");
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { askAssistant } = useChat();

  const isMobile = () =>
    /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;

  // Let the embedding page mirror the query (e.g. to filter its own table).
  useEffect(() => {
    onQueryChange?.(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Server people results, grouped by category in a stable order. The flat
  // keyboard-nav index walks these groups in the same order they render.
  const personGroups = useMemo(() => {
    let base = 0;
    const groups: { type: UserType["type"]; label: string; items: UserType[]; base: number }[] = [];
    for (const g of PERSON_GROUP_ORDER) {
      const items = suggestions.filter((s) => s.type === g.type);
      if (items.length > 0) {
        groups.push({ ...g, items, base });
        base += items.length;
      }
    }
    return groups;
  }, [suggestions]);
  const orderedPeople = useMemo(
    () => personGroups.flatMap((g) => g.items),
    [personGroups]
  );

  // Local (pages/reports/settings/help) results — matched instantly, no network.
  const localResults = useMemo(
    () => (query.trim().length >= 2 ? rankSearchItems(query, pages, 8) : []),
    [query, pages]
  );
  // Group the local results and stamp each group with its flat-index base
  // (people come first in the flat list, then groups in order).
  const localGroups = useMemo(() => {
    let base = suggestions.length;
    const groups: { key: SearchGroup; label: string; items: SearchItem[]; base: number }[] = [];
    for (const g of LOCAL_GROUP_ORDER) {
      const items = localResults.filter((r) => r.group === g.key);
      if (items.length > 0) {
        groups.push({ ...g, items, base });
        base += items.length;
      }
    }
    return groups;
  }, [localResults, suggestions.length]);

  // Flat ordered list for keyboard nav (must match render order).
  const flatEntries = useMemo<FlatEntry[]>(() => {
    const arr: FlatEntry[] = orderedPeople.map((person) => ({ kind: "person", person }));
    for (const g of localGroups) for (const item of g.items) arr.push({ kind: "local", item });
    return arr;
  }, [orderedPeople, localGroups]);

  const hasResults = flatEntries.length > 0;
  // An active query is enough to show the dropdown — so the "Not finding…"
  // row is reachable even when nothing matched (the real "I can't find it"
  // moment).
  const queryActive = query.trim().length >= 2;

  const closeSearchResults = () => {
    setShowResults(false);
    setQuery("");
    setSuggestions([]);
    setActiveIndex(-1);
  };

  // Reset the highlighted row whenever the result set changes.
  useEffect(() => {
    setActiveIndex(-1);
  }, [query]);

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setShowResults(false);
      return;
    }

    // Local results are instant — open immediately while people search debounces.
    setShowResults(true);

    timeoutRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const results = await unifiedSearch(token, query, 25, serverTypes);
        setSuggestions(results as UserType[]);
      } catch (err) {
        captureError(err, { component: "SearchBar", operation: "search" });
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [query]);

  // Click-outside closes (desktop only).
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (!isMobile()) setShowResults(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ⌘K / Ctrl-K focuses the (visible) search box. Two instances exist
  // (mobile/desktop); only the on-screen one has an offsetParent.
  useEffect(() => {
    if (!enableShortcut) return;
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        const el = inputRef.current;
        if (el && el.offsetParent !== null) {
          e.preventDefault();
          el.focus();
          el.select();
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enableShortcut]);

  const goToPerson = (item: UserType) => {
    closeSearchResults();
    // A lead is a membership row (account_type='lead'), so it opens the SAME
    // membership page a member does — notes, tasks, message history, send
    // email. It used to open the Lead Pipeline side panel, so staff converted
    // leads early just to work with them, and conversion invoices the family
    // (Chris, 2026-10-07). The page itself says "this is a lead" and offers
    // Convert to Member (LeadAccountNotice).
    if (item?.type === "member" || item?.type === "lead") {
      navigate(membershipPagePath(tenantId, item.id));
    }
    if (item?.type === "athlete") {
      const athletePath = isMemberRole(userRole)
        ? `/${tenantId}/member/athlete/edit/${item.id}`
        : `/${tenantId}/manage/athletes/edit/${item.id}`;
      navigate(athletePath);
    }
    if (item?.type === "customer") {
      navigate(`/${tenantId}/manage/customers/${item.id}`);
    }
  };

  const goToLocal = (item: SearchItem) => {
    closeSearchResults();
    navigate(item.route);
  };

  const activate = (entry: FlatEntry) => {
    if (entry.kind === "person") goToPerson(entry.person);
    else goToLocal(entry.item);
  };

  // "Not finding what you're looking for?" — hand the current query to the
  // staff AI assistant (opens the chat widget, fresh conversation, prefilled
  // "I'm looking for …" so the user can send/edit). Capture the query before
  // closing clears it.
  const onNotFinding = () => {
    const q = query.trim();
    if (!q) return;
    askAssistant(`I'm looking for ${q}`);
    closeSearchResults();
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      closeSearchResults();
      return;
    }
    if (!showResults || !hasResults) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatEntries.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      const entry = flatEntries[activeIndex] ?? flatEntries[0];
      if (entry) {
        e.preventDefault();
        activate(entry);
      }
    }
  };

  const headerCls = `px-2 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide ${getMutedText(isDarkMode)}`;
  const rowBase = "p-2 cursor-pointer break-words";
  const rowState = (active: boolean) =>
    active ? (isDarkMode ? "bg-gray-700" : "bg-gray-100") : isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100";

  return (
    <div ref={containerRef} className="relative w-full max-w-md mx-auto max-sm:mb-4">
      <input
        aria-label={placeholder}
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        data-onboarding={dataOnboarding}
        className={`px-2 py-1 pr-12 rounded w-full text-sm ${getInputStyles(isDarkMode)}`}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => {
          if (hasResults) setShowResults(true);
        }}
        onKeyDown={onInputKeyDown}
      />
      {!query && (
        <kbd
          className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] px-1.5 py-0.5 rounded border ${getMutedText(isDarkMode)} ${isDarkMode ? "border-gray-600" : "border-gray-300"} hidden sm:block`}
          aria-hidden="true"
        >
          {IS_MAC ? "⌘K" : "Ctrl K"}
        </kbd>
      )}
      {showResults && (hasResults || queryActive) && (
        <div className={`absolute ${getCardBackground(isDarkMode)} border rounded shadow mt-1 z-10 w-full`} style={{ maxHeight: "60vh" }}>
          {isMobile() && (
            <div className={`flex justify-between items-center p-2 border-b ${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}>
              <span className={`text-sm font-medium ${getMutedText(isDarkMode)}`}>Search Results</span>
              <Button
                type="button"
                variant="neutral"
                size="sm"
                iconOnly
                onClick={closeSearchResults}
                aria-label="Close search results"
              >
                <svg className={`w-5 h-5 ${getMutedText(isDarkMode)}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            </div>
          )}
          <div className="overflow-y-auto overflow-x-hidden" style={{ maxHeight: isMobile() ? "calc(60vh - 50px)" : "60vh" }}>
            {/* People (server), grouped by category */}
            {personGroups.map((group) => (
              <div key={group.type}>
                <div className={headerCls}>{group.label}</div>
                <ul>
                  {group.items.map((item, j) => {
                    const idx = group.base + j;
                    return (
                      <li
                        key={`${item?.type}-${item?.id}`}
                        className={`${rowBase} ${rowState(activeIndex === idx)}`}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => goToPerson(item)}
                      >
                        <div className={`text-sm font-medium ${getSecondaryText(isDarkMode)} truncate flex items-center gap-1.5`}>
                          <Tooltip content={item?.status || "unknown"} position="right">
                            <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${getStatusDotColor(item?.status)}`} />
                          </Tooltip>
                          {formatPersonName(item, item?.type === "lead" ? UNNAMED_LEAD : "(no name)")}
                        </div>
                        <div className={`text-sm ${getMutedText(isDarkMode)} truncate`} title={item?.email || undefined}>
                          {/* With no name the email is already the title — don't print it twice. */}
                          {hasName(item) && item?.email ? `${item.email} ` : ""}({item?.type})
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}

            {/* Pages / Reports / Settings / Help (local, instant) */}
            {localGroups.map((group) => (
              <div key={group.key}>
                <div className={headerCls}>{group.label}</div>
                <ul>
                  {group.items.map((item, j) => {
                    const idx = group.base + j;
                    return (
                      <li
                        key={item.id}
                        className={`${rowBase} ${rowState(activeIndex === idx)}`}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => goToLocal(item)}
                      >
                        <div className={`text-sm ${getSecondaryText(isDarkMode)} truncate`}>{item.label}</div>
                        {item.category && (
                          <div className={`text-xs ${getMutedText(isDarkMode)} truncate`}>{item.category}</div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}

            {loading && <div className={`px-2 py-1.5 text-xs ${getMutedText(isDarkMode)}`}>Searching people…</div>}

            {/* Always-present escape hatch: hand the query to the AI assistant. */}
            {queryActive && (
              <button
                type="button"
                onClick={onNotFinding}
                className={`w-full text-left ${rowBase} border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} ${rowState(false)}`}
              >
                <div className={`text-sm font-medium ${getSecondaryText(isDarkMode)} flex items-center gap-1.5`}>
                  <span aria-hidden className="text-purple-500">✨</span>
                  Not finding what you're looking for?
                </div>
                <div className={`text-xs ${getMutedText(isDarkMode)}`}>Ask the assistant for help</div>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchBar;
